import { ChevronIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { parseICloudSharedAlbumLink } from 'helpers/icloud-import';
import { useState } from 'react';
import styles from '../vault-import.module.scss';

interface Props {
  onSubmit: (link: string) => void;
  onBack: () => void;
}

const STEPS = [
  {
    title: 'Create a Shared Album',
    text: 'On your iPhone or Mac, open Photos and add the photos and videos you want into a new Shared Album.',
  },
  {
    title: 'Turn on “Public Website”',
    text: 'Open the album, tap the people icon, and switch on Public Website. Apple creates a link for the album.',
  },
  {
    title: 'Paste the link below',
    text: 'Copy the link and paste it here — we’ll bring the whole album into your vault.',
  },
];

// Paste-a-link screen for iCloud Shared Album imports. Shared albums are the
// only iCloud surface Apple exposes to the web, hence the link-based flow.
export const IcloudLinkForm = ({ onSubmit, onBack }: Props) => {
  const [link, setLink] = useState('');
  const [touched, setTouched] = useState(false);

  const token = parseICloudSharedAlbumLink(link);
  const showInvalid = touched && link.trim().length > 0 && !token;

  const submit = () => {
    setTouched(true);
    if (token) onSubmit(link.trim());
  };

  return (
    <>
      <div className={styles.headerBar}>
        <button
          type="button"
          aria-label="Back"
          className={styles.backBtn}
          onClick={onBack}
        >
          <ChevronIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.headerTitle}>iCloud Shared Album</h1>
        <span className={styles.headerSpacer} aria-hidden />
      </div>

      <div className={styles.body}>
        <div className={styles.icloudCard}>
          <div className={styles.icloudIntro}>
            <span className={styles.icloudTitle}>
              Import from your iPhone in three steps
            </span>
            <p className={styles.icloudSubtitle}>
              iCloud doesn&rsquo;t let apps browse your photos directly —
              instead, you share an album with a link and we do the rest.
            </p>
          </div>

          <ol className={styles.icloudSteps}>
            {STEPS.map((step, index) => (
              <li key={step.title} className={styles.icloudStep}>
                <span className={styles.icloudStepNumber}>{index + 1}</span>
                <span className={styles.icloudStepBody}>
                  <span className={styles.icloudStepTitle}>{step.title}</span>
                  <span className={styles.icloudStepText}>{step.text}</span>
                </span>
              </li>
            ))}
          </ol>

          <label className={styles.icloudField} htmlFor="icloud-link-input">
            <span className={styles.icloudLabel}>Shared album link</span>
            <input
              id="icloud-link-input"
              className={styles.icloudInput}
              placeholder="https://www.icloud.com/sharedalbum/#…"
              value={link}
              onChange={(e) => {
                setLink(e.target.value);
                setTouched(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
            {showInvalid && (
              <span className={styles.icloudError}>
                That doesn&rsquo;t look like a Shared Album link — it should
                start with https://www.icloud.com/sharedalbum/#
              </span>
            )}
          </label>

          <p className={styles.icloudNote}>
            Apple limits shared-album quality (photos up to 2048px, videos up to
            720p). For originals, download the files to your computer and use
            &ldquo;Your Computer&rdquo; instead.
          </p>
        </div>
      </div>

      <div className={styles.bottomBar}>
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.XLarge}
          onClick={submit}
          disabled={!token}
        >
          Continue
        </Button>
      </div>
    </>
  );
};
